/* ==========================================================================
   HAUS BOUTIQUE — CUSTOM JAVASCRIPT
   --------------------------------------------------------------------------
   Loaded as its own module after theme.js, so theme.js can stay an
   unmodified Prestige vendor file. Add new HAUS behaviour here rather than
   editing theme.js directly.
   ========================================================================== */

/* --------------------------------------------------------------------------
   CART GIFT NOTE
   --------------------------------------------------------------------------
   Rendered by snippets/cart-gift-note.liquid in both the cart drawer and the
   full cart page. Saves three plain Shopify cart ATTRIBUTES -- "Gift",
   "Gift To", "Gift From", "Gift Message" -- so they attach to the order as
   a whole (visible under Additional details in Shopify Admin), separate
   from the existing single-field cart note.

   This combines two patterns the theme already ships:
   - The checkbox-reveals-a-fields-block behaviour is the same approach as
     <gift-card-recipient> (js/common/product/gift-card-recipient.js),
     used for the native gift card recipient form on product pages.
   - The save-straight-to-cart/update.js-on-change behaviour is the same
     approach as the built-in <cart-note> element
     (js/common/cart/cart-note.js).

   Persistence across drawer open/close and quantity changes needs nothing
   extra here: cart sections are re-rendered by Shopify's own section
   rendering API whenever the cart changes, and the Liquid snippet always
   reads the current saved attributes straight from `cart.attributes` on
   every render -- this element only needs to handle saving, not restoring.
   -------------------------------------------------------------------------- */

class CartGiftNote extends HTMLElement {
  connectedCallback() {
    this.checkbox = this.querySelector('[name="attributes[Gift]"]');
    this.fields = this.querySelector('.cart-gift-note__fields');
    this.otherInputs = Array.from(
      this.querySelectorAll('[name^="attributes[Gift "]')
    );

    this.checkbox?.addEventListener('change', () => this.onCheckboxChanged());
    this.otherInputs.forEach((input) => {
      input.addEventListener('change', (event) => this.onFieldChanged(event));
    });

    // Reflect the checkbox's actual state on load (matches its `checked`
    // attribute, which the Liquid template already sets from the cart's
    // saved "Gift" attribute) -- keeps the fields block and the disabled
    // state of its inputs in sync with what's visibly checked.
    this.sync();
  }

  sync() {
    const checked = Boolean(this.checkbox?.checked);
    this.otherInputs.forEach((input) => {
      input.disabled = !checked;
    });
    this.fields?.toggleAttribute('hidden', !checked);
  }

  onCheckboxChanged() {
    this.sync();
    this.save('Gift', this.checkbox.checked ? 'Yes' : '');
  }

  onFieldChanged(event) {
    const match = event.target
      .getAttribute('name')
      ?.match(/^attributes\[(.+)\]$/);
    if (!match) return;
    this.save(match[1], event.target.value);
  }

  save(key, value) {
    fetch(`${Shopify.routes.root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { [key]: value } }),
      // Allows the request to complete even if the customer navigates
      // away or closes the drawer right after typing -- same reasoning
      // the theme's own <cart-note> element uses.
      keepalive: true,
    });
  }
}

if (!window.customElements.get('cart-gift-note')) {
  window.customElements.define('cart-gift-note', CartGiftNote);
}
